import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { ArrowBackOutlined, WarningAmberOutlined, SearchOutlined, ClearOutlined, ArrowUpwardOutlined, ArrowDownwardOutlined } from '@mui/icons-material'
import { formatInTimeZone } from 'date-fns-tz'
import { apiService, FraudDetectionResult } from '../services/api'

type SortField = 'name' | 'confidence'
type SortDirection = 'asc' | 'desc'

export default function ScanDetail() {
  const { scanId } = useParams<{ scanId: string }>()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState<SortField>('confidence')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const { data: scan, isLoading, error } = useQuery({
    queryKey: ['scan', scanId],
    queryFn: () => apiService.getScan(scanId!),
    enabled: !!scanId,
  })

  const suspiciousResults = useMemo(() => {
    if (!scan) return []

    let filtered = scan.results.filter((r) => r.is_suspicious && r.confidence_score >= 0.8)

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter((r) =>
        r.candidate_name.toLowerCase().includes(query)
      )
    }

    filtered.sort((a, b) => {
      if (sortField === 'name') {
        const comparison = a.candidate_name.localeCompare(b.candidate_name)
        return sortDirection === 'asc' ? comparison : -comparison
      } else {
        const comparison = a.confidence_score - b.confidence_score
        return sortDirection === 'asc' ? comparison : -comparison
      }
    })

    return filtered
  }, [scan, searchQuery, sortField, sortDirection])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection(field === 'confidence' ? 'desc' : 'asc')
    }
  }

  const getMissingFields = (result: FraudDetectionResult) => {
    const missingReason = result.reasons.find((r) => r.type === 'missing_information')
    if (!missingReason) return null
    const match = missingReason.description.match(/Missing (?:critical )?fields: (.+)/)
    return match ? match[1] : null
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        <Button startIcon={<ArrowBackOutlined />} onClick={() => navigate('/')}>
          Back to Dashboard
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load scan: {(error as Error).message}
        </Alert>
      </Box>
    )
  }

  if (!scan) {
    return (
      <Box>
        <Button startIcon={<ArrowBackOutlined />} onClick={() => navigate('/')}>
          Back to Dashboard
        </Button>
        <Alert severity="info" sx={{ mt: 2 }}>
          Scan not found
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Button startIcon={<ArrowBackOutlined />} onClick={() => navigate('/')} sx={{ mb: 3 }}>
        Back to Dashboard
      </Button>

      <Typography variant="h4" gutterBottom>
        Scan Details
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Scan Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Scan ID:</Typography>
                  <Typography fontFamily="monospace">{scan.scan_id}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Status:</Typography>
                  <Chip label={scan.status} color="success" size="small" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Trigger:</Typography>
                  <Chip label={scan.trigger} size="small" variant="outlined" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Started:</Typography>
                  <Typography>{formatInTimeZone(new Date(scan.started_at), 'America/New_York', 'MMM dd, yyyy HH:mm:ss zzz')}</Typography>
                </Box>
                {scan.completed_at && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Completed:</Typography>
                    <Typography>{formatInTimeZone(new Date(scan.completed_at), 'America/New_York', 'MMM dd, yyyy HH:mm:ss zzz')}</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Summary
              </Typography>
              {scan.summary ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Total Candidates:</Typography>
                    <Typography fontWeight="bold">{scan.summary.total_candidates}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Suspicious:</Typography>
                    <Typography fontWeight="bold" color="error.main">
                      {scan.summary.suspicious_candidates}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Flagged Rate:</Typography>
                    <Typography fontWeight="bold">
                      {(scan.summary.flagged_rate * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography color="text.secondary">No summary available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberOutlined color="error" />
              <Typography variant="h6">Flagged Candidates (80%+ confidence)</Typography>
              <Chip label={suspiciousResults.length} color="error" size="small" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                size="small"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchOutlined sx={{ color: 'text.secondary', mr: 1 }} />,
                  endAdornment: searchQuery && (
                    <ClearOutlined
                      sx={{ color: 'text.secondary', cursor: 'pointer' }}
                      onClick={() => setSearchQuery('')}
                    />
                  ),
                }}
                sx={{ width: 250 }}
              />
            </Box>
          </Box>

          {suspiciousResults.length === 0 ? (
            <Alert severity="success">
              {searchQuery
                ? `No candidates found matching "${searchQuery}"`
                : 'No suspicious candidates detected!'}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        onClick={() => handleSort('name')}
                      >
                        Name
                        {sortField === 'name' && (
                          sortDirection === 'asc' ? <ArrowUpwardOutlined fontSize="small" sx={{ ml: 0.5 }} /> : <ArrowDownwardOutlined fontSize="small" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        onClick={() => handleSort('confidence')}
                      >
                        Confidence
                        {sortField === 'confidence' && (
                          sortDirection === 'asc' ? <ArrowUpwardOutlined fontSize="small" sx={{ ml: 0.5 }} /> : <ArrowDownwardOutlined fontSize="small" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>Reasons</TableCell>
                    <TableCell>Missing Fields</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suspiciousResults.map((result: FraudDetectionResult) => {
                    const missingFields = getMissingFields(result)
                    return (
                      <TableRow key={result.candidate_id}>
                        <TableCell>
                          <Typography fontWeight="medium">{result.candidate_name}</Typography>
                        </TableCell>
                        <TableCell>{result.candidate_email || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={`${(result.confidence_score * 100).toFixed(0)}%`}
                            color="error"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {result.reasons
                              .filter((r) => r.type !== 'missing_information')
                              .map((reason, idx) => (
                                <Chip
                                  key={idx}
                                  label={reason.type.replace(/_/g, ' ')}
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                />
                              ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {missingFields ? (
                            <Chip label={missingFields} size="small" color="error" variant="outlined" />
                          ) : (
                            <Typography color="text.secondary" variant="body2">
                              None
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}
